import React, { useMemo, useRef, type JSX } from 'react';
import classNames from 'classnames';
import {
	CarouselImageFormat,
	CarouselVariant,
	IBaseListAction,
	ICarouselItem,
	IMessageCommandCarouselParameters,
} from '@enuan/chat__utils';
import { CarouselItem } from './CarouselItem';
import { ReactComponent as ArrowIcon } from '@/assets/arrow-down.svg';
// eslint-disable-next-line import/no-webpack-loader-syntax
import slickCss from '!!raw-loader!slick-carousel/slick/slick.css';
// eslint-disable-next-line import/no-webpack-loader-syntax
import slickThemeCss from '!!raw-loader!slick-carousel/slick/slick-theme.css';
import Slider, { ResponsiveObject, Settings } from 'react-slick';
import { uniqueId as _uniqueId } from 'lodash';
import { styled } from '@mui/material/styles';

const PrevArrow = (props: any) => {
	const { className, onClick } = props;
	return (
		<div className={className} onClick={onClick}>
			<ArrowIcon />
		</div>
	);
};

const NextArrow = (props: any) => {
	const { className, onClick } = props;
	return (
		<div className={className} onClick={onClick}>
			<ArrowIcon />
		</div>
	);
};

type Props = Omit<JSX.IntrinsicElements['div'], 'children'> & {
	items: ICarouselItem[];
	settings?: IMessageCommandCarouselParameters['data']['settings'] & Settings;
	onAction?: (action: IBaseListAction) => void;
};

const responsiveSettings: ResponsiveObject[] = [
	{
		breakpoint: 600,
		settings: {
			slidesToShow: 1,
			slidesToScroll: 1,
			dots: false,
			arrows: false,
			centerMode: true,
		},
	},
];

const DEFAULT_CAROUSEL_AUTOPLAY_SPEED = 3000;

export const Carousel = styled(
	React.memo(
		({
			items,
			className,
			settings: {
				variant = CarouselVariant.RICH,
				image_format = CarouselImageFormat.WIDE,
				cover = true,
				margins = false,
				responsive,
				..._settings
			} = {},
			onAction,
			...props
		}: Props) => {
			const ref = useRef<Slider | null>(null);
			const currIndex = useRef(0);
			const paused = useRef(false);
			const loopTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

			const handleMouseEnter = () => {
				paused.current = true;
				clearTimeout(loopTimer.current);
				ref.current?.slickPause();
			};

			const handleMouseLeave = () => {
				paused.current = false;
				ref.current?.slickPlay();
				if (currIndex.current === items.length - 1) {
					loopTimer.current = setTimeout(() => {
						if (!paused.current) {
							ref.current?.slickGoTo(0);
						}
					}, _settings.autoplaySpeed ?? DEFAULT_CAROUSEL_AUTOPLAY_SPEED);
				}
			};

			const settings: Settings = useMemo(
				() => ({
					dots: false,
					infinite: false,
					speed: 500,
					slidesToShow: 1,
					slidesToScroll: 1,
					nextArrow: <NextArrow />,
					prevArrow: <PrevArrow />,
					centerMode: true,
					focusOnSelect: false,
					accessibility: false,
					..._settings,
					afterChange: _settings.autoplay
						? (index) => {
								currIndex.current = index;
								if (paused.current) return;
								if (currIndex.current === items.length - 1) {
									loopTimer.current = setTimeout(() => {
										if (!paused.current) {
											ref.current?.slickGoTo(0);
										}
									}, _settings.autoplaySpeed ?? DEFAULT_CAROUSEL_AUTOPLAY_SPEED);
								}
							}
						: undefined,
					responsive: responsiveSettings.map((resp) => {
						const found = responsive?.find(
							(r) => r.breakpoint === resp.breakpoint,
						);
						if (found && typeof resp.settings !== 'string') {
							return {
								...resp,
								settings: {
									...settings,
									...resp.settings,
									...(typeof found.settings === 'object' ? found.settings : {}),
								},
							};
						}
						return resp;
					}),
				}),
				[_settings, responsive, items.length],
			);

			const isCover = variant === CarouselVariant.RICH && cover;
			const hasMargins = variant === CarouselVariant.RICH && margins;
			const format =
				variant === CarouselVariant.RICH ? image_format : undefined;

			// eslint-disable-next-line react-hooks/exhaustive-deps -- deps trigger re-mount via key change
			const key = useMemo(() => _uniqueId(), [items, settings]);

			return (
				<div
					key={key}
					className={classNames(
						'carousel',
						className,
						`carousel--${variant}`,
						isCover && `carousel--cover`,
						hasMargins && `carousel--with-margins`,
						format && `carousel--${format}`,
					)}
					{...props}
				>
					<style>{slickCss}</style>
					<style>{slickThemeCss}</style>
					<div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
						<Slider {...settings} ref={ref}>
							{items.map((card, i) => (
								<CarouselItem
									key={i}
									item={card}
									variant={variant}
									image_format={format}
									onAction={onAction}
								/>
							))}
						</Slider>
					</div>
				</div>
			);
		},
	),
	{
		label: 'Carousel',
		target: 'Carousel-root',
	},
)({
	margin: '0.5em 1em',
	'& .slick-slider .slick-list .slick-track .slick-slide': {
		height: 'unset',
		'& > div': {
			flex: 1,
			maxHeight: '80vh',
		},
	},
	'& .slick-prev:before, & .slick-next:before': {
		display: 'none',
	},
	'& .slick-track': {
		display: 'flex',
	},
	'& .slick-arrow': {
		fontSize: '1.5em',
		height: '1.8em',
		width: '1.8em',
		opacity: 0.5,
		'& svg': {
			height: '1.8em',
			width: '1.8em',
			fill: 'var(--carousel__arrow_color)',
		},
		'&.slick-prev': {
			left: '-30px',
			'& svg': {
				transform: 'rotate(90deg)',
			},
		},
		'&.slick-next': {
			right: '-30px',
			'& svg': {
				transform: 'rotate(-90deg)',
			},
		},
		'&:hover': {
			opacity: 1,
		},
		'&.slick-disabled': {
			display: 'none',
		},
	},
	'& .slick-slide': {
		display: 'flex',
		transform: 'scale(0.9)',
		'& > div': {
			display: 'flex',
			width: '100%',
		},
	},
});
